// What the phone keeps for offline form fill (round 42).
//
// IndexedDB, written and read by app code, never by the service worker: the
// worker still never touches /api/ (round 14's rule, kept), and data the
// page owns is data the tests can reach. Four stores:
//
//   packs    the offline pack, per account (`tenantId:userId`)
//   meta     the last signed-in session, so the app can open with no signal
//   forms    one record per form being filled in on this phone
//   blobs    photo and signature bytes, by file id, apart from the record so
//            an answer typed per keystroke does not rewrite a photo each time
//
// Behind an interface with an in-memory twin: jsdom has no IndexedDB, and a
// browser that refuses it (some private modes) still gets a working form,
// just not one that survives a reload with no signal. No wrapper library:
// the four calls this needs do not earn a dependency.
import type { OfflinePack } from '../formsApi';
import type { Tenant, User } from '../api';
import type { LocalForm } from './localForm';

export interface StoredPack {
  owner: string;
  pack: OfflinePack;
  /** When THIS phone fetched it, beside the server's generatedAt. */
  savedAt: string;
}

export interface SessionSnapshot {
  user: User;
  tenant: Tenant;
  savedAt: string;
}

export interface OfflineStore {
  getPack(owner: string): Promise<StoredPack | undefined>;
  putPack(value: StoredPack): Promise<void>;
  /** Signing out takes the company's lists off a shared phone. */
  deletePack(owner: string): Promise<void>;
  getSession(): Promise<SessionSnapshot | undefined>;
  putSession(value: SessionSnapshot): Promise<void>;
  clearSession(): Promise<void>;
  getForm(id: string): Promise<LocalForm | undefined>;
  listForms(owner: string): Promise<LocalForm[]>;
  /**
   * Read-modify-write in ONE transaction. The page typing and the sync engine
   * recording a step both write the same record, and a plain get-then-put
   * from each would let one quietly undo the other.
   */
  updateForm(id: string, change: (current: LocalForm) => LocalForm): Promise<LocalForm | undefined>;
  putForm(value: LocalForm): Promise<void>;
  deleteForm(id: string): Promise<void>;
  getBlob(id: string): Promise<Blob | undefined>;
  putBlob(id: string, blob: Blob): Promise<void>;
  deleteBlob(id: string): Promise<void>;
}

// --- In memory ---------------------------------------------------------

export function memoryStore(): OfflineStore {
  const packs = new Map<string, StoredPack>();
  const forms = new Map<string, LocalForm>();
  const blobs = new Map<string, Blob>();
  let session: SessionSnapshot | undefined;
  // Structured-clone semantics, so a caller mutating what it read cannot
  // change what is stored: the IndexedDB version behaves that way, and a
  // test passing only because the twin shares objects would be decoration.
  const copy = <T,>(value: T): T => structuredClone(value);
  return {
    getPack: async (owner) => (packs.has(owner) ? copy(packs.get(owner)!) : undefined),
    putPack: async (value) => void packs.set(value.owner, copy(value)),
    deletePack: async (owner) => void packs.delete(owner),
    getSession: async () => (session ? copy(session) : undefined),
    putSession: async (value) => void (session = copy(value)),
    clearSession: async () => void (session = undefined),
    getForm: async (id) => (forms.has(id) ? copy(forms.get(id)!) : undefined),
    listForms: async (owner) => [...forms.values()].filter((f) => f.owner === owner).map(copy),
    updateForm: async (id, change) => {
      const current = forms.get(id);
      if (!current) return undefined;
      const next = change(copy(current));
      forms.set(id, copy(next));
      return copy(next);
    },
    putForm: async (value) => void forms.set(value.id, copy(value)),
    deleteForm: async (id) => void forms.delete(id),
    getBlob: async (id) => blobs.get(id),
    putBlob: async (id, blob) => void blobs.set(id, blob),
    deleteBlob: async (id) => void blobs.delete(id),
  };
}

// --- IndexedDB ---------------------------------------------------------

const DB_NAME = 'dayboard-offline';
const DB_VERSION = 1;

function promised<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
  });
}

function openDb(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      db.createObjectStore('packs', { keyPath: 'owner' });
      db.createObjectStore('meta');
      const forms = db.createObjectStore('forms', { keyPath: 'id' });
      forms.createIndex('owner', 'owner');
      db.createObjectStore('blobs');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function indexedDbStore(factory: IDBFactory): OfflineStore {
  let opening: Promise<IDBDatabase> | null = null;
  const db = () => (opening ??= openDb(factory));

  async function read<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
    const tx = (await db()).transaction(store, 'readonly');
    return (await promised(tx.objectStore(store).get(key))) as T | undefined;
  }
  async function write(store: string, value: unknown, key?: IDBValidKey): Promise<void> {
    const tx = (await db()).transaction(store, 'readwrite');
    tx.objectStore(store).put(value, key);
    await done(tx);
  }
  async function remove(store: string, key: IDBValidKey): Promise<void> {
    const tx = (await db()).transaction(store, 'readwrite');
    tx.objectStore(store).delete(key);
    await done(tx);
  }

  return {
    getPack: (owner) => read<StoredPack>('packs', owner),
    putPack: (value) => write('packs', value),
    deletePack: (owner) => remove('packs', owner),
    getSession: () => read<SessionSnapshot>('meta', 'session'),
    putSession: (value) => write('meta', value, 'session'),
    clearSession: () => remove('meta', 'session'),
    getForm: (id) => read<LocalForm>('forms', id),
    listForms: async (owner) => {
      const tx = (await db()).transaction('forms', 'readonly');
      return (await promised(tx.objectStore('forms').index('owner').getAll(owner))) as LocalForm[];
    },
    updateForm: async (id, change) => {
      const tx = (await db()).transaction('forms', 'readwrite');
      const store = tx.objectStore('forms');
      const current = (await promised(store.get(id))) as LocalForm | undefined;
      if (!current) {
        await done(tx);
        return undefined;
      }
      const next = change(current);
      store.put(next);
      await done(tx);
      return next;
    },
    putForm: (value) => write('forms', value),
    deleteForm: (id) => remove('forms', id),
    getBlob: (id) => read<Blob>('blobs', id),
    putBlob: (id, blob) => write('blobs', blob, id),
    deleteBlob: (id) => remove('blobs', id),
  };
}

// --- The one the app uses ----------------------------------------------

/**
 * IndexedDB, falling back to memory for good if the database will not OPEN
 * (some private-browsing modes refuse it). Without this, every write on such
 * a browser would fail and the form would not save at all; with it, the form
 * works exactly as before round 42 while the page is open, and simply cannot
 * outlive a reload with no signal. Individual failures after a successful
 * open are left to the caller: those are real errors, not a missing feature.
 */
export function resilientStore(primary: OfflineStore, probe: () => Promise<unknown>): OfflineStore {
  const fallback = memoryStore();
  let chosen: Promise<OfflineStore> | null = null;
  const pick = () =>
    (chosen ??= probe().then(
      () => primary,
      () => fallback
    ));
  const wrap =
    <K extends keyof OfflineStore>(key: K) =>
    (async (...args: unknown[]) => {
      const store = await pick();
      return (store[key] as (...a: unknown[]) => unknown)(...args);
    }) as OfflineStore[K];
  return {
    getPack: wrap('getPack'),
    putPack: wrap('putPack'),
    deletePack: wrap('deletePack'),
    getSession: wrap('getSession'),
    putSession: wrap('putSession'),
    clearSession: wrap('clearSession'),
    getForm: wrap('getForm'),
    listForms: wrap('listForms'),
    updateForm: wrap('updateForm'),
    putForm: wrap('putForm'),
    deleteForm: wrap('deleteForm'),
    getBlob: wrap('getBlob'),
    putBlob: wrap('putBlob'),
    deleteBlob: wrap('deleteBlob'),
  };
}

let current: OfflineStore | null = null;

/** IndexedDB where the browser has it, memory where it does not. */
export function offlineStore(): OfflineStore {
  if (!current) {
    if (typeof indexedDB === 'undefined') {
      current = memoryStore();
    } else {
      const idb = indexedDbStore(indexedDB);
      current = resilientStore(idb, () => idb.getSession());
    }
  }
  return current;
}

/** Tests only: a fresh store, so one test's forms cannot leak into the next. */
export function resetOfflineStore(store: OfflineStore = memoryStore()): OfflineStore {
  current = store;
  return store;
}

/**
 * Asks the browser not to clear this site's storage under pressure, once
 * there is something on the phone worth keeping. Best effort and silent:
 * browsers grant it on their own terms (an installed app, a bookmarked site),
 * and a refusal changes nothing about how the form works.
 */
export function askToPersist(): void {
  try {
    void navigator.storage?.persist?.().catch(() => undefined);
  } catch {
    // no storage manager: nothing to ask
  }
}
